/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cross2Icon, PersonIcon, ShuffleIcon, TargetIcon } from '@radix-ui/react-icons';
import { Button, Select, Text } from '@/shared/ui';
import { FilterPanelShell } from '@/shared/ui/components/composed/filter-panel/FilterPanelShell';
import { viewportOverlayStyle } from '@/shared/ui/utils/viewport-overlay';
import { ChipBtn, FieldLabel, SectionHeader } from '@features/property-management/components/filter-panel/parts';
import { useDealStageLabels } from '@features/deals/constants/deal-stages';
import type { DealStage } from '@features/deals/constants/deal-stages';
import { SELECTABLE_ORIGIN_CHANNELS } from '../constants/origin-channels';
import {
  BROKER_FILTER_ENABLED,
  FILTERABLE_STAGES,
  type AttendanceFilters,
} from '../constants/attendance-filters';

/** A selectable agency member, in the shape the agency store already exposes. */
export interface BrokerOption {
  id: string;
  name: string;
}

export interface AttendancesFilterPanelProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: AttendanceFilters) => void;
  onClear: () => void;
  initialFilters?: AttendanceFilters;
  brokers?: BrokerOption[];
}

/**
 * The same full-height overlay the property filter panel uses.
 *
 * Copied deliberately rather than abstracted: `FilterPanelShell` is the
 * shared part, and the three listing pages pass it the same geometry so
 * they look like one product.
 */
const PANEL_STYLE: React.CSSProperties = {
  maxWidth: '340px',
  height: '100%',
  background: 'var(--surface-primary)',
  boxShadow: 'var(--shadow-lg)',
  fontFamily: 'var(--font-family)',
};

const BODY_STYLE: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: 'var(--space-4)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-5)',
};

/**
 * Count of filters that would actually narrow the board.
 *
 * @param filters - The panel's current filter state.
 * @returns How many of them are set.
 */
function countActive(filters: AttendanceFilters): number {
  return [
    filters.originChannel,
    filters.stages?.length ? 'set' : '',
    filters.brokerId,
  ].filter(Boolean).length;
}

/**
 * The stage chips — one per stage the board shows, multi-select.
 *
 * @param props - The selected stages and the toggle handler.
 * @returns The stage section of the filter panel.
 */
function StageSection({
  selected,
  onToggle,
}: {
  selected: readonly string[];
  onToggle: (stage: string) => void;
}) {
  const { t } = useTranslation('features/attendances');
  const stageLabels = useDealStageLabels();
  return (
    <div>
      <SectionHeader
        icon={<TargetIcon width={14} height={14} />}
        title={t('filterPanel.stage')}
      />
      <div className="flex flex-wrap gap-(--space-2)">
        {FILTERABLE_STAGES.map((stage) => (
          <ChipBtn
            key={stage}
            value={stageLabels[stage as DealStage] ?? stage}
            active={selected.includes(stage)}
            onClick={() => onToggle(stage)}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * The Atendimentos filter overlay: etapa, canal de origem and corretor.
 *
 * Values are staged locally and handed over on apply, so half-made
 * selections never refetch the board — the same contract the property and
 * client panels have.
 *
 * @param props - Open state, the apply/clear/close callbacks, the filters
 *   to open with, and the agency's members for the corretor select.
 * @returns The filter panel, or null while closed.
 */
export const AttendancesFilterPanel: React.FC<AttendancesFilterPanelProps> = ({
  open,
  onClose,
  onApply,
  onClear,
  initialFilters = {},
  brokers = [],
}) => {
  const { t } = useTranslation('features/attendances');
  const [filters, setFilters] = useState<AttendanceFilters>(initialFilters);

  const toggleStage = (stage: string) => {
    const current = filters.stages ?? [];
    setFilters({
      ...filters,
      stages: current.includes(stage)
        ? current.filter((s) => s !== stage)
        : [...current, stage],
    });
  };

  const activeCount = countActive(filters);

  if (!open) return null;

  const channelOptions = SELECTABLE_ORIGIN_CHANNELS.map((channel) => ({
    value: channel,
    label: t(`filters.channels.${channel}`),
  }));

  return (
    <FilterPanelShell
      testIdPrefix="attendances-"
      usePortal
      layout="nested"
      title={t('filterPanel.title')}
      onClose={onClose}
      onClear={() => { setFilters({}); onClear(); }}
      onApply={() => { onApply(filters); onClose(); }}
      activeCount={activeCount}
      clearLabel={t('filterPanel.clear')}
      applyLabel={t('filterPanel.applyWithCount', { count: activeCount })}
      closeIcon={<Cross2Icon width={14} height={14} />}
      ButtonComponent={Button}
      TextComponent={({ children, style, className }) => (
        <Text
          as="span"
          className={className ?? 'text-(length:--text-body-sm) font-bold'}
          style={style ?? { color: 'var(--seed-base)' }}
        >
          {children}
        </Text>
      )}
      backdropStyle={viewportOverlayStyle}
      panelClassName="flex w-full flex-col overflow-hidden"
      panelStyle={PANEL_STYLE}
      headerClassName="flex items-center justify-between px-(--space-4) pt-(--space-1) pb-(--space-2)"
      headerStyle={{ borderBottom: '1px solid var(--border-subtle)' }}
      closeButtonClassName="flex items-center justify-center w-7 h-7 rounded-(--radius-md) transition-colors hover:bg-[var(--surface-hover)]"
      closeButtonStyle={{ border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer' }}
      bodyStyle={BODY_STYLE}
      footerClassName="flex gap-(--space-2) px-(--space-4) py-(--space-3) sm:pb-(--space-3) pb-(--space-20)"
      footerStyle={{ borderTop: '1px solid var(--border-subtle)' }}
      clearButtonClassName="flex-1 py-(--space-2) text-(length:--text-body-sm) font-semibold rounded-(--radius-md) cursor-pointer"
      clearButtonStyle={{ background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)', fontFamily: 'var(--font-family)' }}
      applyButtonClassName="flex-1 py-(--space-2) text-(length:--text-body-sm) font-bold rounded-(--radius-md) cursor-pointer"
      applyButtonStyle={{ background: 'var(--button-primary-bg)', color: 'var(--button-primary-text)', border: 'none', fontFamily: 'var(--font-family)' }}
    >
      <StageSection selected={filters.stages ?? []} onToggle={toggleStage} />

      <div>
        <SectionHeader
          icon={<ShuffleIcon width={14} height={14} />}
          title={t('filterPanel.originChannel')}
        />
        <FieldLabel>{t('filterPanel.originChannelLabel')}</FieldLabel>
        <Select
          name="originChannel"
          data={channelOptions}
          value={filters.originChannel || null}
          onChange={(value: string | string[] | null) => setFilters({
            ...filters,
            originChannel: Array.isArray(value) ? value[0] : (value ?? ''),
          })}
          placeholder={t('filters.allChannels')}
        />
      </div>

      {BROKER_FILTER_ENABLED && (
        <div data-testid="attendances-filter-broker">
          <SectionHeader
            icon={<PersonIcon width={14} height={14} />}
            title={t('filterPanel.broker')}
          />
          <FieldLabel>{t('filterPanel.brokerLabel')}</FieldLabel>
          <Select
            name="brokerId"
            data={brokers.map((broker) => ({ value: broker.id, label: broker.name }))}
            value={filters.brokerId || null}
            onChange={(value: string | string[] | null) => setFilters({
              ...filters,
              brokerId: Array.isArray(value) ? value[0] : (value ?? ''),
            })}
            placeholder={t('filterPanel.allBrokers')}
          />
        </div>
      )}
    </FilterPanelShell>
  );
};

export default AttendancesFilterPanel;
